/** Admin search form state and category-specific filter helpers. */

import type { Search } from '@/api/types';
import type { MeProfile } from '@/api/types';
import { isUnlimitedSourceLimit } from '@/utils/sourceLimits';
import { defaultSearchVisibility, normalizeSearchVisibility, type SearchVisibility } from '@/utils/searchVisibility';
import {
  getFilterFieldKeys,
  getMinMaxPairLabels,
  getSearchCategoryDef,
  isApartmentCategory,
  isGenericCategory,
  isLandCategory,
  isVehicleCategory,
  type SearchFilterFieldDef,
} from './searchCategoryConfig';

export type SearchAdminFormState = {
  name: string;
  category: string;
  source_names: string[];
  visibility: SearchVisibility;
  search_text: string;
  brand: string;
  model: string;
  trim: string;
  body_type: string;
  generation: string;
  vehicle_make_id: string;
  vehicle_model_id: string;
  vehicle_trim_id: string;
  year_min: string;
  year_max: string;
  price_min: string;
  price_max: string;
  mileage_min: string;
  mileage_max: string;
  fuel: string;
  transmission: string;
  message_templates: string;
  city: string;
  region: string;
  property_type: '' | 'apartment' | 'house';
  listing_type: '' | 'rent' | 'sale';
  sqm_min: string;
  sqm_max: string;
  rooms_min: string;
  rooms_max: string;
  floor_min: string;
  floor_max: string;
};

export const EMPTY_SEARCH_FORM: SearchAdminFormState = {
  name: '',
  category: 'vehicles',
  source_names: [],
  visibility: 'private',
  search_text: '',
  brand: '',
  model: '',
  trim: '',
  body_type: '',
  generation: '',
  vehicle_make_id: '',
  vehicle_model_id: '',
  vehicle_trim_id: '',
  year_min: '',
  year_max: '',
  price_min: '',
  price_max: '',
  mileage_min: '',
  mileage_max: '',
  fuel: '',
  transmission: '',
  message_templates: '',
  city: '',
  region: '',
  property_type: '',
  listing_type: '',
  sqm_min: '',
  sqm_max: '',
  rooms_min: '',
  rooms_max: '',
  floor_min: '',
  floor_max: '',
};

const META_KEYS = new Set<keyof SearchAdminFormState>(['name', 'category', 'source_names', 'visibility']);

export const SEARCH_FORM_FILTER_KEYS = Object.keys(EMPTY_SEARCH_FORM).filter(
  (k) => !META_KEYS.has(k as keyof SearchAdminFormState),
) as (keyof SearchAdminFormState)[];

const ALL_FILTER_KEYS = SEARCH_FORM_FILTER_KEYS;

export type SearchFormSource = Pick<
  Search,
  'name' | 'category' | 'source_names' | 'filters_json' | 'visibility'
>;

/** Hydrate admin form filter fields from a search row (no cross-search leakage). */
export function searchToAdminForm(search: SearchFormSource): SearchAdminFormState {
  const parsed = parseSearchFilters(search.category, search.filters_json);
  const filterState = { ...EMPTY_SEARCH_FORM };
  for (const key of ALL_FILTER_KEYS) {
    const value = parsed[key];
    if (value !== undefined) {
      (filterState[key] as string) = String(value);
    }
  }
  return {
    ...filterState,
    name: search.name,
    category: search.category,
    source_names: [...search.source_names],
    visibility: normalizeSearchVisibility(search.visibility),
  };
}

/** Clone a search into create form state (new row, copied filters). */
export function duplicateSearchToAdminForm(search: SearchFormSource): SearchAdminFormState {
  const base = searchToAdminForm(search);
  const copyName = search.name.trim().endsWith(' (copy)')
    ? search.name.trim()
    : `${search.name.trim()} (copy)`;
  return {
    ...base,
    name: copyName,
  };
}

export function resetSearchFormForCreate(
  category = 'vehicles',
  me?: MeProfile | null,
): SearchAdminFormState {
  return {
    ...EMPTY_SEARCH_FORM,
    category,
    visibility: defaultSearchVisibility(me),
  };
}

function numOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

function emptyFieldValue(key: keyof SearchAdminFormState): SearchAdminFormState[keyof SearchAdminFormState] {
  if (key === 'property_type' || key === 'listing_type') return '';
  return '';
}

function serializeFieldValue(
  field: SearchFilterFieldDef,
  raw: SearchAdminFormState[keyof SearchAdminFormState],
): unknown {
  if (field.serializeAs === 'number') {
    return numOrUndefined(String(raw ?? ''));
  }
  if (field.serializeAs === 'stringList') {
    const lines = String(raw ?? '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    return lines.length ? lines : undefined;
  }
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return undefined;
  return trimmed;
}

function parseFieldValue(field: SearchFilterFieldDef, filters: Record<string, unknown>): string {
  const raw = filters[field.filterKey];
  if (field.serializeAs === 'stringList') {
    return Array.isArray(raw) ? raw.map(String).join('\n') : '';
  }
  if (raw == null) return '';
  return String(raw);
}

/** Clear fields that do not apply after a category change. */
export function clearIrrelevantFields(
  form: SearchAdminFormState,
  category: string,
): SearchAdminFormState {
  const allowed = new Set<string>(getFilterFieldKeys(category));
  const next = { ...form, category };
  for (const key of ALL_FILTER_KEYS) {
    if (!allowed.has(key)) {
      (next[key] as SearchAdminFormState[typeof key]) = emptyFieldValue(key) as SearchAdminFormState[typeof key];
    }
  }
  return next;
}

export function buildSearchFilters(form: SearchAdminFormState): Record<string, unknown> {
  const def = getSearchCategoryDef(form.category);
  if (!def) return {};

  const f: Record<string, unknown> = {};
  for (const field of def.fields) {
    const value = serializeFieldValue(field, form[field.key]);
    if (value !== undefined) {
      f[field.filterKey] = value;
    }
    if (field.catalogIdKey) {
      const catalogId = String(form[field.catalogIdKey] ?? '').trim();
      if (catalogId) {
        f[field.catalogIdKey] = catalogId;
      }
    }
  }
  return f;
}

export function parseSearchFilters(
  category: string,
  filters: Record<string, unknown> | null | undefined,
): Partial<SearchAdminFormState> {
  const def = getSearchCategoryDef(category);
  const f = filters ?? {};
  if (!def) {
    return {
      search_text: f.search_text != null ? String(f.search_text) : '',
      price_min: f.price_min != null ? String(f.price_min) : '',
      price_max: f.price_max != null ? String(f.price_max) : '',
    };
  }

  const parsed: Partial<SearchAdminFormState> = {};
  for (const field of def.fields) {
    (parsed as Record<string, string>)[field.key] = parseFieldValue(field, f);
    if (field.catalogIdKey) {
      const rawId = f[field.catalogIdKey];
      (parsed as Record<string, string>)[field.catalogIdKey] =
        rawId != null ? String(rawId) : '';
    }
  }
  return parsed;
}

function validateMinMaxPairs(form: SearchAdminFormState, category: string): string | null {
  const def = getSearchCategoryDef(category);
  if (!def) return null;

  const pairs = new Map<string, { min?: number; max?: number }>();
  for (const field of def.fields) {
    if (!field.minMaxPair || field.serializeAs !== 'number') continue;
    const n = numOrUndefined(String(form[field.key] ?? ''));
    const entry = pairs.get(field.minMaxPair) ?? {};
    if (field.filterKey.endsWith('_min')) entry.min = n;
    if (field.filterKey.endsWith('_max')) entry.max = n;
    pairs.set(field.minMaxPair, entry);
  }

  for (const [pairId, { min, max }] of Array.from(pairs.entries())) {
    if (min != null && max != null && min > max) {
      const labels = getMinMaxPairLabels(pairId);
      return `${labels.min} cannot exceed ${labels.max.toLowerCase()}`;
    }
  }
  return null;
}

export function validateSearchForm(
  form: SearchAdminFormState,
  opts?: {
    enforcePlanLimits?: boolean;
    maxSourcesPerSearch?: number;
    accessibleSources?: number;
    maxSearches?: number;
    searchCount?: number;
  },
): string | null {
  if (!form.name.trim()) return 'Name is required';
  if (!form.source_names.length) return 'Select at least one source';
  if (opts?.enforcePlanLimits === false) {
    return validateMinMaxPairs(form, form.category);
  }
  const maxPerSearch = opts?.maxSourcesPerSearch;
  if (
    maxPerSearch != null &&
    !isUnlimitedSourceLimit(maxPerSearch) &&
    form.source_names.length > maxPerSearch
  ) {
    if (opts?.accessibleSources != null && opts.accessibleSources > maxPerSearch) {
      return `You can access ${opts.accessibleSources} source${opts.accessibleSources === 1 ? '' : 's'}. Your plan allows selecting up to ${maxPerSearch} sources per search.`;
    }
    return `Your plan allows up to ${maxPerSearch} sources per search.`;
  }
  if (
    opts?.maxSearches != null &&
    opts.searchCount != null &&
    opts.searchCount >= opts.maxSearches
  ) {
    return `Your plan allows up to ${opts.maxSearches} searches.`;
  }
  return validateMinMaxPairs(form, form.category);
}

export {
  isApartmentCategory,
  isGenericCategory,
  isLandCategory,
  isVehicleCategory,
};
